import React, { useEffect, useState } from 'react'
import {
   Text,
   Box,
   Grid,
   Tooltip,
   Flex,
   Button,
   Modal,
   ModalOverlay,
   ModalBody,
   ModalCloseButton,
   ModalContent,
   ModalFooter,
   ModalHeader
} from '@rockr/rock-ui'
import swal from 'sweetalert'
import { Icon } from '@chakra-ui/core'
import { useFormikContext } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import { Autocomplete } from '../../../components/Autocomplete'
import { IntegerInput } from '../../../components/IntegerInput'
import { MoneyInput } from '../../../components/MoneyInput'
import {
  TableHeader,
  TableContainer,
  TableRow,
  TableData
} from '../../../components/Table'
import { RootState } from '../../../shared/store'
import { fetchSkus } from '../../../shared/store/skus/thunk'
import { fetchBundles } from '../../../shared/store/bundles/thunk'
import { Sku, OfferInput, StepOffers } from '../../../shared/types/skus'
import { Bundle } from '../../../shared/types/bundles'
import { brlFormatter } from '../../../shared/helpers/formatters'
import { ProductStreamsTable } from '../../../components/ProductStreamsTable'
import { addOffer, editOffer, removeOffer } from '../../../shared/store/offers/actions'
import { findBundleById, getBundleTotal } from '../../../shared/helpers/bundle'
import { findSkuByOfferSkuId } from '../../../shared/helpers/sku'
import { getTotalValue, getTotalValueInstallment } from '../../../shared/helpers/offer'
import { OfferTag } from '../../../components/OfferTag'
import { CustomIcon } from '../../../components/CustomIcon'
import { VerticalMenu } from '../../../components/VerticalMenu'
import { PercentInput } from '../../../components/PercentInput'
import { createGenericTypesService } from '../../../shared/api/generic-types'
import { GenericTypes } from '../../../shared/types/generic-types'


const bundleOfferTypeId = Number(process.env.REACT_APP_BUNDLE_OFFER_TYPE_ID!)

type Option = {
  value: any
  label: string
}

const currencies = [
  { value: 'brl', label: 'BRL' },
  { value: 'usd', label: 'USD' }
]

const useOfferTypes = () => {
  const [offerTypes, setOfferTypes] = useState<Option[]>([])

  const formatGenericTypesToSelectFormat = (
    genericType: GenericTypes
  ): Option => ({
    label: genericType.description,
    value: genericType.id
  })

  useEffect(() => {
    const genericTypesService = createGenericTypesService()

    genericTypesService.getOfferTypes()
      .then(offerTypes => offerTypes.map(formatGenericTypesToSelectFormat))
      .then(offerTypes => setOfferTypes(offerTypes))
  }, [])

  return offerTypes
}


export const Quoting = () => {
  const dispatch = useDispatch()
  const skus = useSelector<RootState, Sku[]>(state => state.skus)
  const bundles = useSelector<RootState, Bundle[]>(state => state.bundles)
  const offers = useSelector<RootState, OfferInput[]>(state => state.offers)
  const {
    values,
    setValues,
    setFieldValue,
    handleChange,
    resetForm
  } = useFormikContext<StepOffers>()
  const offerTypes = useOfferTypes()

  const [selectedSku, setSelectedSku] = useState<Sku>()
  const [selectedBundle, setSelectedBundle] = useState<Bundle>()
  const [offerType, setOfferType] = useState<number>()
  const [indexItemToEdit, setIndexItemToEdit] = useState<number>()
  const [itemToEdit, setItemToEdit] = useState<OfferInput>()
  const [indexToRemove, setIndexToRemove] = useState<number | undefined>(undefined)
  
  useEffect(() => {
    if (skus.length === 1) {
      setFieldValue('input.offerSkuId', skus[0].offer_sku_id)
    }
  }, [skus, setFieldValue])

  useEffect(() => {
    if (!offerType) {
      return
    }

    if (offerType !== bundleOfferTypeId) {
      dispatch(fetchSkus(offerType))
      setValues({
        ...values,
        input: {
          ...values.input,
          bundleId: undefined
        }
      })
    }

    if (offerType === bundleOfferTypeId) {
      dispatch(fetchBundles())
      setValues({
        ...values,
        input: {
          ...values.input,
          offerSkuId: undefined
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, offerType])

  useEffect(() => {
    const { offerSkuId, bundleId } = values.input

    if (selectedSku?.offer_sku_id !== offerSkuId) {
      const sku = findSkuByOfferSkuId(skus, offerSkuId)
      setSelectedSku(sku)
    }

    if (selectedBundle?.id !== bundleId) {
      const bundle = findBundleById(bundles, bundleId)
      setSelectedBundle(bundle)
    }
  }, [values, selectedBundle, selectedSku, skus, bundles])

  useEffect(() => {
    const updateOffersFormikValues = () => {
      setValues({
        ...values,
        offers
      })
    }

    updateOffersFormikValues()
  // eslint-disable-next-line
  }, [offers])

  useEffect(() => {
    const minDuration = getContractMinDuration()
    setFieldValue('input.duration', minDuration)
  // eslint-disable-next-line
  }, [selectedSku, offerType])

  const dealValueIsInvalid = () => {
    const { dealValue } = values.input

    if (offerType !== bundleOfferTypeId && selectedSku) {
      const lesserThanMinValue = dealValue < selectedSku?.min_value
      const greaterThanMaxValue = dealValue > selectedSku?.max_value

      return lesserThanMinValue || greaterThanMaxValue
    }

    return false
  }

  const getOfferValues = () => {
    if (offerType === bundleOfferTypeId && selectedBundle) {
      const skuMinValue = selectedBundle.skus.find(sku => sku.minValue > 0)
      const skuMaxValue = selectedBundle.skus.find(sku => sku.maxValue > 0)

      return {
        minValue: skuMinValue?.minValue ?? 0,
        maxValue: skuMaxValue?.maxValue ?? getBundleTotal(selectedBundle)
      }
    }

    if (offerType !== bundleOfferTypeId && selectedSku) {
      return {
        minValue: selectedSku.min_value,
        maxValue: selectedSku.max_value
      }
    }

    return {
      minValue: 0,
      maxValue: 0
    }
  }

  const formatSkusToSelectFormat = (skus: Sku[]) => {
    return skus.map(sku => ({
      value: sku.offer_sku_id,
      label: sku.sku_display_name
    }))
  }

  const formatBundlesToSelectFormat = (bundles: Bundle[]) => {
    return bundles.filter(b => b.visible).map(bundle => ({
      value: bundle.id,
      label: bundle.name
    }))
  }

  const addNewOffer = () => {
    const { input } = values
    const sku = findSkuByOfferSkuId(skus, input.offerSkuId)
    const bundle = bundles.find(b => b.id === input.bundleId)
    const skuCode = sku?.sku_code
    const displayName = sku?.sku_display_name ?? bundle?.name

    const bundleExists = offers.find(
      offer =>
      offer.bundleId && !offer.isChurn && offer.bundleId === input.bundleId
    )
    if(bundleExists) {
      swal({
        title: "Duplicated item",
        text: "You already selected this bundle",
        icon: "error"
      })
      return
    }

    dispatch(addOffer({
      ...values.input,
      skuCode,
      displayName,
      dealValue: values.input.dealValue || 0,
      listPrice: values.input.listPrice || 0,
      isRecurring: ['SFW'].includes(skuCode!) || Boolean(bundle),
      isRenewable: ['SFW'].includes(skuCode!) || Boolean(bundle),
      skus: bundle?.skus,
      payment: {},
      minValue: sku?.min_value,
      maxValue: sku?.max_value
    }))
    const { duration } = values.input
    resetForm()

    setFieldValue('input.duration', duration)
  }

  const getOffersList = () => {
    /* return [] */
    if (offerType === bundleOfferTypeId) {
      return formatBundlesToSelectFormat(bundles)
    }

    return formatSkusToSelectFormat(skus)
  }

  const getOfferInputName = () => {
    if (offerType === bundleOfferTypeId) {
      return 'input.bundleId'
    }

    return 'input.offerSkuId'
  }

  const getDefaultPlanValue = () => {
    if (offerType !== bundleOfferTypeId && selectedSku) {
      return selectedSku.plan_value
    }

    if (offerType === bundleOfferTypeId && selectedBundle) {
      return getBundleTotal(selectedBundle)
    }

    return 0
  }

  const getContractMinDuration = () => {
    if (offerType === bundleOfferTypeId || selectedSku?.sku_code === 'SFW') {
      return 12
    }

    return 1
  }

  const resetItemToEdit = () => {
    setIndexItemToEdit(undefined)
    setItemToEdit(undefined)
  }

  const saveEditedItem = (index: number) => {
    dispatch(editOffer({
      indexTable: index,
      data: itemToEdit!
    }))
    resetItemToEdit()
  }

  const renderItemActions = (offer: OfferInput, index: number) => {
    if (indexItemToEdit === index) {
      const dealValueIsInvalid = itemToEdit!.dealValue < (offer.minValue || 0)
      const invalidFields = itemToEditIsInvalid(offer)
      return (
       <Box display='flex'>
        <Button marginRight={1}  bg="#F2F6FA" onClick={resetItemToEdit}>
          <CustomIcon name="back" boxSize="22px"/>
        </Button>

        <Button
          disabled={dealValueIsInvalid || invalidFields.length > 0}
          bg="#f7f7f7f5"
          onClick={() => saveEditedItem(index)}
        >
          <CustomIcon name="check" boxSize="22px" />
        </Button>
       </Box>
      )
    }

    return (
      <VerticalMenu items={[
        {
          label: 'Edit item',
          icon: 'pencil',
          onClick: () => {
            setIndexItemToEdit(index)
            setItemToEdit(offer)
          }
        },
        {
          label: 'Delete item',
          icon: 'trash',
          onClick: () => setIndexToRemove(index)
        }
      ]} />
    )
  }

  const renderItemQuantity = (offer: OfferInput, index: number) => {
    if(indexItemToEdit === index) {
      return (
        <IntegerInput
          name="itemEditQuantity"
          textAlign='center'
          value={offer.quantity}
          onChange={(e) => {
              const number = Number(e.target.value)
              setItemToEdit({
                ...itemToEdit!,
                quantity: number <= 0 ? 1 : number
              })
            }}
        />
      )
    }
    return offer.quantity
  }

  const renderItemDuration = (offer: OfferInput, index: number) => {
    if (indexItemToEdit === index) {
      return (
        <IntegerInput
          name="itemEditDuration"
          textAlign='center'
          value={offer.duration}
          onChange={(e) => {
            const number = Number(e.target.value)
            setItemToEdit({
              ...itemToEdit!,
              duration: number <= 0 ? 1 : number
            })
          }}
        />
      )
    }
    return offer.duration
  }


  const validateEditDealValue = (offer: OfferInput) => {
    const isLess = itemToEdit?.dealValue!  < (offer.minValue || 0)
    const isGreater = itemToEdit?.dealValue! > (offer.maxValue || itemToEdit?.listPrice!)

    return {
      isLess,
      isGreater
    }
  }

  const validateEditIndex = (offer: OfferInput) => {
    return itemToEdit?.index! < (offer.originalIndex || 1)
  }

  const itemToEditIsInvalid = (offer: OfferInput): string[] => {
    const invalid = []
    const dealValueIsInvalid = validateEditDealValue(offer)
    const indexIsInvalid = validateEditIndex(offer)

    if (dealValueIsInvalid.isGreater || dealValueIsInvalid.isLess) {
      invalid.push('itemEditDealValue')
    }

    if (indexIsInvalid) {
      invalid.push('itemEditIndex')
    }

    return invalid
  }

  const renderItemDealValue = (offer: OfferInput, index: number) => {
    if (!itemToEdit) {
      return brlFormatter.format(offer.dealValue / 100)
    }

    if (indexItemToEdit === index) {
      const invalidFields = itemToEditIsInvalid(offer)
      const validateDealValue = validateEditDealValue(offer)
      return (
        <>
          <MoneyInput
            isInvalid={invalidFields.includes('itemEditDealValue')}
            name="itemEditDealValue"
            currency='BRL'
            value={offer.dealValue}
            onChange={(e) => {
              setItemToEdit({
                ...itemToEdit!,
                dealValue: Number(e.target.value)
              })
            }}
          />

          {validateDealValue.isLess && (
            <Text fontSize={10} color='red.500' mt={1}>
              Deal value is less than the min. value
            </Text>
          )}

          {validateDealValue.isGreater && (
            <Text fontSize={10} color='red.500' mt={1}>
              Deal value is greater than the max. value
            </Text>
          )}
        </>
      )
    }

    return brlFormatter.format(offer.dealValue / 100)
  }

  const renderItemIndexPercentage = (offer: OfferInput, index: number) => {
    if(indexItemToEdit === index) {
      const invalidFields = itemToEditIsInvalid(offer)
      return (
        <>
          <PercentInput
            disabled={!Boolean(offer.id)}
            name="itemEditIndex"
            value={offer.index}
            onChange={(e) => {
              setItemToEdit({
                ...itemToEdit!,
                index: Number(e.target.value)
              })
            }}
            isInvalid={invalidFields.includes('itemEditIndex')}
          />

          {invalidFields.includes('itemEditIndex') && (
            <Text fontSize={10} color='red.500' mt={1}>
              Index must be greater than or equal to 100% or the item's last index value
            </Text>
          )}
        </>
      )
    }

    return `${(offer.index * 100).toFixed(2)}%`
  }

  const renderOffer = (offer: OfferInput, index: number) => {
    if (offer.isChurn) {
      return
    }

    const totalValue = getTotalValue(offer)

    return(
      <>
        <TableRow key={index}>
          <TableData align='center'>
            {renderItemQuantity(offer, index)}
          </TableData>
          <TableData align='left'>
            {offer.displayName} {'\u00A0'}
            <OfferTag offer={offer} />
          </TableData>
          <TableData>
            {renderItemDuration(offer, index)}
          </TableData>
          <TableData>
            {renderItemIndexPercentage(offer, index)}
          </TableData>
          <TableData>
            {brlFormatter.format(offer.listPrice / 100)}
          </TableData>
          <TableData>
            {renderItemDealValue(offer, index)}
          </TableData>
          <TableData>
            {brlFormatter.format(totalValue / 100)}
          </TableData>
          <TableData>
            {renderItemActions(offer, index)}
          </TableData>
        </TableRow>

        {offer.skus?.map((sku, index) => (
          <TableRow key={index}>
            <TableData>
              <div />
            </TableData>
            <TableData align='left'>
              {sku.skuPlanName}
            </TableData>
            <TableData>
              {offer.duration}
            </TableData>
            <TableData>
              <div />
            </TableData>
            <TableData>
              {brlFormatter.format(sku.value / 100)}
            </TableData>
            <TableData>
              {brlFormatter.format(sku.value / 100)}
            </TableData>
            <TableData>
              {brlFormatter.format(sku.value / 100)}
            </TableData>
            <TableData>
              <div />
            </TableData>
          </TableRow>
        ))}
      </>
    )
  }

  const renderOfferRemoveModal = () => {
    return (
      <Modal isOpen={indexToRemove === undefined ? false : true} onClose={() => {
        setIndexToRemove(undefined)
      }}>
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>Remove offer</ModalHeader>
            <ModalCloseButton />
            <ModalBody>Do you really want to remove this item?</ModalBody>

            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={() => {
                setIndexToRemove(undefined)
              }}>
                Close
              </Button>
              <Button variant="ghost" onClick={() => {
                dispatch(removeOffer(indexToRemove!))
                setIndexToRemove(undefined)
              }}>Yes</Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    )
  }

  const calculateMultiplier = (
    index: number,
    quantity: number,
    duration: number
  ): number => index * quantity

  const { minValue, maxValue } = getOfferValues()
  const minValueFormatted = brlFormatter.format(minValue / 100)
  const maxValueFormatted = brlFormatter.format(maxValue / 100)
  const durationIsInvalid = values.input.duration < getContractMinDuration()

  return (
    <section>
      <Grid gridGap={4}>
        <Grid templateColumns=".2fr .8fr" gridGap={4}>
          <Box>
            <Text fontSize='sm'>Currency</Text>
            <Autocomplete
              name="currency"
              options={currencies}
              isDisabled
              value={currencies[0].value}
            />
          </Box>

          <Box>
            <Text fontSize='sm'>Select the type of offer you wish to add</Text>
            <Autocomplete
              name="input.offerType"
              options={offerTypes}
              onChange={e => setOfferType(e.target.value)}
              value={offerType}
            />
          </Box>
        </Grid>

        <Grid templateColumns=".8fr .2fr " gridGap={4}>
          <Box>
            <Text fontSize='sm'>Select the offer you with to add</Text>
            <Autocomplete
              value={values.input.offerSkuId || values.input.bundleId}
              name={getOfferInputName()}
              options={getOffersList()}
              onChange={handleChange}
            />
          </Box>

          <Box>
            <Text fontSize='sm'>Quantity</Text>
            <IntegerInput
              min={1}
              name='input.quantity'
              value={values.input.quantity}
              onChange={handleChange}
            />
          </Box>
        </Grid>

        <Grid templateColumns='repeat(5, 1fr)' gridGap={4}>
          <Box>
            <Text fontSize='sm'>Duration (months)</Text>
            <IntegerInput
              isInvalid={durationIsInvalid}
              name='input.duration'
              value={values.input.duration}
              onChange={handleChange}
              disabled={offerType === bundleOfferTypeId}
            />

            {durationIsInvalid && (
              <Text fontSize='xs' color='red.500'>
                Minimum duration: {getContractMinDuration()}
              </Text>
            )}
          </Box>

          <Box>
            <Text fontSize='sm'>Index</Text>
            <PercentInput
              name="input.index"
              value={values.input.index}
              disabled
            />
          </Box>

          <Box>
            <Text fontSize='sm'>List Price</Text>
            <MoneyInput
              name="input.listPrice"
              currency='BRL'
              disabled
              value={getDefaultPlanValue()}
              onChange={handleChange}
            />
          </Box>

          <Box>
            <Text fontSize='sm'>
              Deal Value {'\u00A0'}
              <Tooltip label={`Min value: ${minValueFormatted} / Max value: ${maxValueFormatted}`} placement='auto'>
                <Icon name='help' />
              </Tooltip>
            </Text>
            <MoneyInput
              isInvalid={dealValueIsInvalid()}
              name="input.dealValue"
              currency='BRL'
              value={getDefaultPlanValue()}
              onChange={handleChange}
            />

            {dealValueIsInvalid() && (
              <Text fontSize='xs' color='red.500'>
                Deal value is invalid
              </Text>
            )}
          </Box>

          <Box>
            <Text fontSize='sm'>Total Value</Text>
            <MoneyInput
              name="input.totalValue"
              currency='BRL'
              value={getTotalValue(values.input) || 0}
              disabled
            />
          </Box>
        </Grid>

        <Flex justify='flex-end' width='100%'>
          <Flex>
            <Button
              onClick={addNewOffer}
              leftIcon={<CustomIcon name='add' />}
              colorScheme='blue'
              isDisabled={dealValueIsInvalid() || Boolean(!selectedSku && !selectedBundle) || durationIsInvalid}
            >
              Add offer
            </Button>
          </Flex>
        </Flex>

        <Box>
          <Text marginBottom={6}>Client’s Offers</Text>

          <TableContainer fullWidth>
            <TableRow>
              <TableHeader align='left' label="Qty" />
              <TableHeader align='left' label="Item" />
              <TableHeader label="Duration (months)" />
              <TableHeader label="Index" />
              <TableHeader label="List Price" />
              <TableHeader label="Deal Value" />
              <TableHeader label="Total Value" />
              <TableHeader label=""/>
            </TableRow>

            {offers.map(renderOffer)}
          </TableContainer>
        </Box>

        <Box marginTop={12}>
          <Text marginBottom={6}>
            Deal Totals {'\u00A0'}
            <Tooltip label='One installment value' placement='auto'>
              <Icon name='help' />
            </Tooltip>
          </Text>

          <ProductStreamsTable
            skus={skus}
            offers={offers}
            calculateMultiplier={calculateMultiplier}
            calculateTotal={getTotalValueInstallment}
          />
        </Box>
      </Grid>
      {renderOfferRemoveModal()}
    </section>
  )
}
